import { createApolloProvider } from "@vue/apollo-option"
import { InMemoryCache, ApolloClient, createHttpLink, DefaultOptions } from "@apollo/client/core"
import { setContext } from "@apollo/client/link/context"
import ls from "local-storage"
import { isDev } from "@/helpers"
import { generateMutation, generateSimpleIListQuery, generateSimpleMutation, generateSimpleQuery } from "@/helpers/introspection-query"
import { DebugLink } from "@storipress/apollo-vue-devtool"
import { cloneDeep } from "lodash"

export const DEV_API_URL = "http://localhost:3004"
export const PROD_API_URL = "https://promtcraft.herokuapp.com"

// HTTP connection to the API
const devHttpLink = createHttpLink({
    // You should use an absolute URL here
    uri: DEV_API_URL + "/graphql",
})

const prodHttpLink = createHttpLink({
    // You should use an absolute URL here
    uri: PROD_API_URL + "/graphql",
})

const authLink = setContext((_, { headers }) => {
    // get the authentication token from local storage if it exists
    const token = ls("token") as unknown as string
    // return the headers to the context so httpLink can read them
    return {
        headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : "",
        }
    }
})

// Cache implementation
const cache = new InMemoryCache()

const defaultOptions: DefaultOptions = {
    watchQuery: {
        fetchPolicy: "no-cache",
        errorPolicy: "ignore",
    },
    query: {
        fetchPolicy: "no-cache",
        errorPolicy: "all",
    },
}

// Create the apollo client
export const devApolloClient = new ApolloClient({
    link: authLink.concat(devHttpLink).concat(new DebugLink()),
    cache,
    connectToDevTools: true,
    defaultOptions
})
export const prodApolloClient = new ApolloClient({
    link: authLink.concat(prodHttpLink).concat(new DebugLink()),
    cache,
    connectToDevTools: true,
    defaultOptions
})

export const apolloClient = isDev() ? devApolloClient : prodApolloClient

export const apolloProvider = createApolloProvider({
    defaultClient: apolloClient,
})

export async function queryIList (name: string, fields?: string[], params?: {[key: string]: any}): Promise<any> {
    const query = generateSimpleIListQuery(name, fields || [], params)
    const res = await apolloClient.query({
        query,
    })
    if (!res.data && res.errors) {
        throw new Error(res.errors.toLocaleString())
    }
    return cloneDeep(res.data[name])
}

export async function query (name: string, fields?: string[], params?: {[key: string]: any}): Promise<any> {
    const query = generateSimpleQuery(name, fields || [], params)
    const res = await apolloClient.query({
        query,
    })
    if (!res.data && res.errors) {
        throw new Error(res.errors.toLocaleString())
    }
    return cloneDeep(res.data[name])
}

export async function mutation (name: string, vars: {[key: string]: any}, fields?: string[], opts?: {inputName?: string, varQuery?: string}): Promise<any> {
    const mutation = generateMutation(name, vars, fields || [], opts)
    const res = await apolloClient.mutate({
        mutation: mutation,
        variables: {
            [opts?.inputName || (name + "Input")]: vars
        }
    })
    if (!res.data && res.errors) {
        throw new Error(res.errors.toLocaleString())
    }
    return res.data[name]
}

export async function simpleMutation (name: string, varName: string, value: string, fields?: string[]): Promise<any> {
    const mutation = generateSimpleMutation(name, varName, value)
    const res = await apolloClient.mutate({
        mutation: mutation,
        variables: {

        }
    })
    if (!res.data && res.errors) {
        throw new Error(res.errors.toLocaleString())
    }
    return res.data[name]
}
